/**
 * Scroll Lock
 * To be used along with ScrollLock scss mixin.
 *
 * @author    David Alkire <dalkire@wayfair.com>
 * @author    Calvin Hou <chou@wayfair.com>
 * @author    Matthew Hamlin <mhamlin@wayfair.com>
 * @copyright 2017 Wayfair LLC - All rights reserved
 */
import {isiOS} from '../feature-detect';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';

const SCROLL_LOCK_CLASSNAME = 'u-noScroll';
const SCROLL_LOCK_STYLES = `overflow-y: hidden; position: relative; max-height: 100vh;`;

let lockCount = 0;
let styleHistory = null;

export const styleObjectToAttr = (obj) =>
  // { background: 'red', 'overflow-y': 'none'}
  Object.entries(obj)
    // sort keys alphabetically
    .sort(([a], [b]) => {
      if (a > b) {
        return 1;
      }
      if (a < b) {
        return -1;
      }
      return 0;
    })
    // format key value pair as css declaration
    .map(([key, val]) => `${key}: ${val};`)
    .join(' ');

export const styleAttrToObject = (str) =>
  str // "background: red; overflow-y: none;"
    .split(';') // ['background: red', 'overflow-y: none', '']
    .filter(Boolean) // remove empty string entries
    .map((rule) =>
      rule
        // [['background', ' red'],['overflow-y', ' none']]
        .split(':')
        // [['background', 'red', 'overflow-y', 'none']]
        .map((segment) => segment.trim())
    )
    // { background: 'red', 'overflow-y': 'none'}
    .reduce((acc, [key, val]) => ({...acc, [key]: val}), {});

export const mergeStyleStrings = (a, b) =>
  styleObjectToAttr({
    ...styleAttrToObject(a),
    ...styleAttrToObject(b),
  });

const isClient =
  typeof window !== 'undefined' && typeof document !== 'undefined';

const scrollBarPadding = () => {
  return window.innerWidth - document.body.clientWidth;
};

const lock = (rootElement, targetElements = []) => {
  // check to see if the lock class is already present in cases of stacking
  // multiple lock calls
  const isAlreadyLocked = !!lockCount;
  // most of the time this will be null so fallback to empty string for parsing
  const originalStyle = rootElement.getAttribute('style');
  const originalAsString = originalStyle || '';
  // in the event we're already locked we don't want to overwrite the original
  // inline style which we will restore when we unlock
  if (!isAlreadyLocked) {
    styleHistory = originalStyle;
  }
  const padAmount = scrollBarPadding();
  const paddingRight = padAmount > 0 ? `padding-right: ${padAmount}px;` : '';
  const requiredStyles = `${SCROLL_LOCK_STYLES} ${paddingRight}`;
  const mergedStyles = mergeStyleStrings(originalAsString, requiredStyles);
  rootElement.setAttribute('style', mergedStyles);
  rootElement.classList.add(SCROLL_LOCK_CLASSNAME);
  targetElements.forEach((el) => {
    disableBodyScroll(el, {
      allowTouchMove: (eventTarget) => {
        // All elements within will automatically be allowed to scroll
        // taken from https://github.com/willmcpo/body-scroll-lock/issues/100#issuecomment-480150763
        while (eventTarget && eventTarget !== document.body) {
          if (!!eventTarget.dataset.allowTouchMove) {
            return true;
          }
          eventTarget = eventTarget.parentNode;
        }
      },
    });
  });
};

const unlock = (rootElement, targetElements = []) => {
  rootElement.classList.remove(SCROLL_LOCK_CLASSNAME);
  rootElement.removeAttribute('style');
  if (styleHistory) {
    // in cases where there is a prevoius style, restore it. In cases where
    // styleHistory is the null default, calling setAttribute here sets the
    // attribute to a string value of "null"
    rootElement.setAttribute('style', styleHistory);
  }
  targetElements.forEach(enableBodyScroll);
};

const handleAddScroll = (targetElements = []) =>
  lock(isiOS() ? document.documentElement : document.body, targetElements);

const handleRemoveScroll = (targetElements = []) =>
  unlock(isiOS() ? document.documentElement : document.body, targetElements);

// Scroll Lock Helper
// A global proxy to make sure lock integrity is preserved
export const requestScrollLock = (targetElements = []) => {
  // Only run on the client
  if (isClient) {
    handleAddScroll(targetElements);
    lockCount += 1;
  }
};

export const requestScrollUnlock = (targetElements = []) => {
  // Only run on the client
  if (isClient && lockCount !== 0) {
    lockCount -= 1;
    if (lockCount === 0) {
      handleRemoveScroll(targetElements);
      // If the lock count is 0, then lets be safe and clear
      // all locks that might have been enabled
      clearAllBodyScrollLocks();
    }
  }
};
